//
// https://github.com/achmadk/open-source-projects/tree/main/packages/react-loading-overlay
//
import { css, cx, keyframes } from '@emotion/css';
import { memo, useEffect, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';

const rotate360 = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const spinnerDash = keyframes`
  0% {
    stroke-dasharray: 1,200;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 89,200;
    stroke-dashoffset: -35px;
  }
  100% {
    stroke-dasharray: 89,200;
    stroke-dashoffset: -124px;
  }
`;

export const defaultStyles = {
  wrapper: state => ({ position: 'relative', ...state }),
  overlay(state, props) {
    return {
      position: 'absolute',
      height: '100%',
      width: '100%',
      top: '0px',
      left: '0px',
      display: 'flex',
      textAlign: 'center',
      fontSize: '1.2em',
      color: '#FFF',
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      zIndex: 800,
      transition: `opacity ${props?.fadeSpeed ?? 500}ms ease-in`,
      opacity: ['entering', 'entered'].includes(state) ? 1 : 0,
    };
  },
  content: () => ({ margin: 'auto' }),
  spinner: () => ({
    position: 'relative',
    margin: '0px auto 10px auto',
    width: '100px',
    maxHeight: '100%',
    '&:before': { content: '""', display: 'block', paddingTop: '100%' },
    '& svg': {
      animation: `${rotate360} 2s linear infinite`,
      height: '100%',
      transformOrigin: 'center center',
      width: '100%',
      position: 'absolute',
      top: '0',
      bottom: '0',
      left: '0',
      right: '0',
      margin: 'auto',
      '& circle': {
        animation: `${spinnerDash} 1.5s ease-in-out infinite`,
        strokeDasharray: '1,200',
        strokeDashoffset: 0,
        strokeLinecap: 'round',
        stroke: '#FFF',
      },
    },
  }),
};

export const LoadingOverlay = memo(props => {
  const {
    ref,
    children,
    className,
    onClick,
    spinner = true,
    fadeSpeed = 500,
    active = true,
    styles = {},
    classNamePrefix = '_loading_overlay_',
    text,
  } = props;

  const [overflowCSS, setOverflowCSS] = useState(undefined);
  const wrapperEl = useRef(null);
  const overlayRef = useRef(null);

  const context = (names, ...args) => {
    const arr = Array.isArray(names) ? names : [names];

    return cx(
      arr.map(name => (typeof name === 'string' ? `${classNamePrefix}${name}` : '')),
      ...args,
    );
  };
  const getStyles = (key, providedState) => {
    const base = defaultStyles?.[key]?.(providedState, props);
    const custom = styles?.[key] ?? false;

    if (!custom) return base;

    return typeof custom === 'function' ? custom(base, props) : custom;
  };

  useEffect(() => {
    wrapperEl.current = overlayRef.current?.parentElement ?? null;
    if (wrapperEl.current !== null) {
      const wrapperStyle = window.getComputedStyle(wrapperEl.current);
      const ofCSS = ['overflow', 'overflowX', 'overflowY'].reduce((m, i) => {
        // eslint-disable-next-line no-param-reassign
        if (wrapperStyle[i] !== 'visible') m[i] = 'hidden';

        return m;
      }, {});

      setOverflowCSS(ofCSS);
    }
  }, []);

  useEffect(() => {
    if (active && wrapperEl.current) {
      wrapperEl.current.scrollTop = 0;
    }
  }, [active]);

  return (
    <div
      data-testid="wrapper"
      ref={ref}
      className={context(
        ['wrapper', active && 'wrapper--active'],
        css(getStyles('wrapper', active ? overflowCSS : undefined)),
        className,
      )}
    >
      <CSSTransition
        in={active}
        classNames="_loading-overlay-transition"
        timeout={fadeSpeed}
        unmountOnExit
        nodeRef={overlayRef}
      >
        {state => (
        /* eslint-disable */ 
          <div
            ref={overlayRef}
            data-testid="overlay"
            className={context('overlay', css(getStyles('overlay', state)))}
            onClick={onClick}
          >
            <div className={context('content', css(getStyles('content')))}>
              {spinner &&
                (typeof spinner === 'boolean' ? (
                 <div className={context('spinner', css(getStyles('spinner')))}>
                    <svg viewBox="25 25 50 50">
                      <circle
                        cx="50"
                        cy="50"
                        r="20"
                        fill="none"
                        strokeWidth="2"
                        strokeMiterlimit="10"
                      />
                    </svg>
                  </div>
                ) : (
                  spinner
                ))}
              {text}
            </div>
          </div>
        /* eslint-enable */
        )}
      </CSSTransition>
      {children}
    </div>
  );
});
